import React from 'react';
import { useAuthStore } from '../stores/authStore';
import { isUserWhitelisted, createCheckoutSession } from '../services/firebase';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';

const SubscriptionPage = () => {
  const { userProfile, user } = useAuthStore();
  const [isWhitelisted, setIsWhitelisted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  // Check if user is whitelisted (gets free premium)
  useEffect(() => {
    const checkWhitelist = async () => {
      if (user?.email) {
        const whitelisted = await isUserWhitelisted(user.email);
        setIsWhitelisted(whitelisted);
      }
      setLoading(false);
    };
    checkWhitelist();
  }, [user]);

  const isPremium = userProfile?.isPremium || isWhitelisted;

  const handleUpgrade = async () => {
    if (!user) {
      toast.error('Please sign in to upgrade');
      return;
    }

    try {
      setCheckoutLoading(true);

      // Get the current URL for success/cancel redirects
      const baseUrl = window.location.origin;

      // Call Firebase Function to create Stripe checkout session
      const result = await createCheckoutSession({
        userId: user.uid,
        email: user.email,
        successUrl: `${baseUrl}/dashboard?payment=success`,
        cancelUrl: `${baseUrl}/subscription?payment=cancelled`
      });

      if (result.data.url) {
        // Redirect to Stripe checkout
        window.location.href = result.data.url;
      } else {
        throw new Error('Failed to create checkout session');
      }
    } catch (error) {
      console.error('Error creating checkout session:', error);
      toast.error('Failed to start checkout. Please try again.');
      setCheckoutLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: 'var(--bg-secondary)', padding: '2rem 0' }}>
        <div className="container" style={{ maxWidth: '600px', textAlign: 'center' }}>
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  // Already Premium
  if (isPremium) {
    return (
      <div style={{ minHeight: '100vh', background: 'var(--bg-secondary)', padding: '2rem 0' }}>
        <div className="container" style={{ maxWidth: '600px' }}>
          <div className="card" style={{ padding: '3rem', textAlign: 'center' }}>
            <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>💎</div>
            <h1 style={{ marginBottom: '1rem' }}>You're Premium!</h1>
            <p style={{ color: 'var(--text-secondary)', marginBottom: '2rem' }}>
              {isWhitelisted
                ? "You have complimentary premium access as a beta tester."
                : "Thank you for supporting Charades Party!"}
            </p>

            <div style={{
              background: 'var(--bg-tertiary)',
              padding: '1.5rem',
              borderRadius: 'var(--radius-lg)',
              marginBottom: '2rem'
            }}>
              <h3 style={{ marginBottom: '1rem' }}>Your Premium Benefits:</h3>
              <ul style={{ listStyle: 'none', textAlign: 'left' }}>
                {[
                  '✓ Unlimited games',
                  '✓ All word packs & themes',
                  '✓ Up to 20 players per game',
                  '✓ Custom word packs',
                  '✓ Game history & stats',
                  '✓ No ads',
                  '✓ Priority support'
                ].map((feature, i) => (
                  <li key={i} style={{ marginBottom: '0.5rem', color: 'var(--success)', fontWeight: 600 }}>
                    {feature}
                  </li>
                ))}
              </ul>
            </div>

            <a href="/dashboard" className="btn btn-primary btn-full btn-lg">
              Back to Dashboard
            </a>
          </div>
        </div>
      </div>
    );
  }

  // Not Premium - Show Upgrade Options
  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-secondary)', padding: '2rem 0' }}>
      <div className="container" style={{ maxWidth: '900px' }}>
        <h1 style={{ textAlign: 'center', marginBottom: '3rem' }}>
          💎 Upgrade to Premium
        </h1>

        {/* Comparison Table */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '1.5rem',
          marginBottom: '3rem'
        }}>
          {/* Free Plan */}
          <div className="card" style={{ padding: '2rem' }}>
            <h2 style={{ marginBottom: '1rem', fontSize: '1.5rem' }}>Free</h2>
            <div style={{ fontSize: '2.5rem', fontWeight: 800, marginBottom: '1rem' }}>
              $0
            </div>
            <div style={{ color: 'var(--text-secondary)', marginBottom: '2rem', minHeight: '3rem' }}>
              Perfect for casual players
            </div>
            <ul style={{ listStyle: 'none', marginBottom: '2rem' }}>
              {[
                { text: '3 free games', available: true },
                { text: 'Classic word pack', available: true },
                { text: 'Up to 20 players', available: true },
                { text: 'All word packs', available: false },
                { text: 'Unlimited games', available: false },
                { text: 'Custom word packs', available: false },
                { text: 'Game history', available: false }
              ].map((feature, i) => (
                <li key={i} style={{
                  marginBottom: '0.75rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  opacity: feature.available ? 1 : 0.4
                }}>
                  <span style={{ color: feature.available ? 'var(--success)' : 'var(--text-tertiary)' }}>
                    {feature.available ? '✓' : '—'}
                  </span>
                  <span>{feature.text}</span>
                </li>
              ))}
            </ul>
            <div style={{
              padding: '1rem',
              background: 'var(--bg-tertiary)',
              borderRadius: 'var(--radius)',
              textAlign: 'center',
              fontSize: '0.875rem',
              color: 'var(--text-secondary)'
            }}>
              Current Plan
            </div>
          </div>

          {/* Premium Plan */}
          <div className="card" style={{
            padding: '2rem',
            border: '3px solid var(--primary)',
            position: 'relative',
            boxShadow: '0 10px 40px rgba(102, 126, 234, 0.3)'
          }}>
            <div style={{
              position: 'absolute',
              top: '-12px',
              right: '20px',
              background: 'var(--primary)',
              color: 'white',
              padding: '0.25rem 1rem',
              borderRadius: 'var(--radius)',
              fontSize: '0.75rem',
              fontWeight: 700
            }}>
              BEST VALUE
            </div>

            <h2 style={{ marginBottom: '1rem', fontSize: '1.5rem' }}>Premium</h2>
            <div style={{ fontSize: '2.5rem', fontWeight: 800, marginBottom: '0.25rem' }}>
              $4.99
            </div>
            <div style={{ color: 'var(--text-secondary)', marginBottom: '0.5rem' }}>
              per year
            </div>
            <div style={{
              fontSize: '0.75rem',
              color: 'var(--success)',
              marginBottom: '2rem',
              fontWeight: 600,
              minHeight: '3rem'
            }}>
              Just $0.42/month! Save 92% vs monthly pricing
            </div>
            <ul style={{ listStyle: 'none', marginBottom: '2rem' }}>
              {[
                'Unlimited games',
                'Classic word pack',
                'Up to 20 players',
                'All premium word packs',
                'No game limits',
                'Custom word packs',
                'Full game history & stats'
              ].map((feature, i) => (
                <li key={i} style={{
                  marginBottom: '0.75rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  fontWeight: 600
                }}>
                  <span style={{ color: 'var(--success)' }}>✓</span>
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
            <button
              onClick={handleUpgrade}
              disabled={checkoutLoading}
              className="btn btn-primary btn-full btn-lg"
              style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                border: 'none',
                opacity: checkoutLoading ? 0.7 : 1
              }}
            >
              {checkoutLoading ? 'Loading...' : 'Upgrade Now 🚀'}
            </button>
          </div>
        </div>

        {/* Trust Signals */}
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '2rem',
            flexWrap: 'wrap',
            fontSize: '0.875rem',
            color: 'var(--text-secondary)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span>🔒</span>
              <span>Secure Checkout</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span>💳</span>
              <span>Credit Card or PayPal</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span>🔄</span>
              <span>Cancel Anytime</span>
            </div>
          </div>
        </div>

        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <p style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)' }}>
            Powered by Stripe • Secure payment processing
          </p>
        </div>

        <div style={{ textAlign: 'center' }}>
          <a href="/dashboard" style={{ color: 'var(--primary)', textDecoration: 'none' }}>
            ← Back to dashboard
          </a>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionPage;
